/**
 * @fileoverview Walkthrough Renderer
 * @description Top-level component that renders the active walkthrough.
 * Automatically selects the right UI (tooltip tour vs. modal dialog)
 * based on the current step type. Mount once in the provider tree.
 * @module features/walkthrough/components/WalkthroughRenderer
 */

import { useWalkthrough } from '../hooks/useWalkthrough';
import { WalkthroughOverlay } from './WalkthroughOverlay';
import { WalkthroughTooltip } from './WalkthroughTooltip';
import { WalkthroughDialog } from './WalkthroughDialog';

export function WalkthroughRenderer() {
  const {
    activeWalkthrough,
    activeProgress,
    currentStep,
    currentStepIndex,
    totalSteps,
    isActive,
    nextStep,
    prevStep,
    dismiss,
    complete,
  } = useWalkthrough();

  if (!isActive || !currentStep || !activeWalkthrough || !activeProgress) {
    return null;
  }

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const completedSteps = activeProgress.completedSteps;

  // Modal/checklist type → render as dialog
  if (currentStep.type === 'modal' || currentStep.type === 'checklist') {
    return (
      <WalkthroughDialog
        open={isActive}
        step={currentStep}
        steps={activeWalkthrough.steps}
        stepIndex={currentStepIndex}
        totalSteps={totalSteps}
        completedSteps={completedSteps}
        onNext={nextStep}
        onPrev={prevStep}
        onDismiss={dismiss}
        onComplete={complete}
        isLastStep={isLastStep}
        isFirstStep={isFirstStep}
        walkthroughName={activeWalkthrough.name}
      />
    );
  }

  // Tooltip/beacon type → render with overlay + floating tooltip
  return (
    <>
      <WalkthroughOverlay
        targetSelector={currentStep.target}
        visible={isActive}
        onBackdropClick={dismiss}
      />
      <WalkthroughTooltip
        step={currentStep}
        stepIndex={currentStepIndex}
        totalSteps={totalSteps}
        onNext={nextStep}
        onPrev={prevStep}
        onDismiss={dismiss}
        isLastStep={isLastStep}
        isFirstStep={isFirstStep}
      />
    </>
  );
}
